/** Shapes returned by the m24-ehealth and m25-public-health APIs (fields the portal uses). */

/** Generic cursor-page envelope shared by every list endpoint in both modules. */
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

// ─────────────────────────────────────────────────────────────────────────
// m24-ehealth
// ─────────────────────────────────────────────────────────────────────────

export type FacilityKind = 'CLINIC' | 'HOSPITAL' | 'LAB_PARTNER';
export const FACILITY_KINDS: FacilityKind[] = ['CLINIC', 'HOSPITAL', 'LAB_PARTNER'];

export interface Facility {
  id: string;
  name: string;
  district: string;
  kind: FacilityKind;
  address?: string | null;
  specialties: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export interface FacilitySlot {
  id: string;
  facilityId: string;
  specialty: string;
  startsAt: string;
  durationMinutes: number;
  capacity: number;
  bookedCount: number;
  teleconsult: boolean;
  /** Added by GET /facilities/:id/slots (capacity - bookedCount). */
  spotsRemaining?: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export type BloodGroup = 'A_POS' | 'A_NEG' | 'B_POS' | 'B_NEG' | 'AB_POS' | 'AB_NEG' | 'O_POS' | 'O_NEG' | 'UNKNOWN';
export const BLOOD_GROUPS: BloodGroup[] = ['A_POS', 'A_NEG', 'B_POS', 'B_NEG', 'AB_POS', 'AB_NEG', 'O_POS', 'O_NEG', 'UNKNOWN'];

export interface Allergy {
  substance: string;
  severity: 'MILD' | 'MODERATE' | 'SEVERE';
  reaction?: string;
  notedAt?: string;
}

export interface EmergencyContact {
  name: string;
  relation: string;
  phone: string;
}

export interface HisLink {
  facilityId: string;
  facilityName: string;
  hisId: string;
}

/** Master Patient Index — GET /patients/:nin (clinical-staff lookup, access-logged). */
export interface PatientIndex {
  id: string;
  nin: string;
  hisIds: HisLink[];
  bloodGroup?: BloodGroup | null;
  allergies: Allergy[];
  emergencyContact?: EmergencyContact | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export type AppointmentStatus = 'BOOKED' | 'CANCELLED' | 'COMPLETED' | 'NO_SHOW';

export interface Appointment {
  id: string;
  nin: string;
  facilityId: string;
  facility?: Facility;
  slotId: string;
  slot?: FacilitySlot;
  specialty: string;
  referralId?: string | null;
  status: AppointmentStatus;
  teleconsult: boolean;
  teleconsultUrl?: string | null;
  teleconsultToken?: string | null;
  cancelReason?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

/** GET /facilities/:id/slots response shape (open slots list). */
export interface SlotsListResponse {
  items: FacilitySlot[];
}

export type ReferralPriority = 'ROUTINE' | 'URGENT' | 'EMERGENCY';
export const REFERRAL_PRIORITIES: ReferralPriority[] = ['ROUTINE', 'URGENT', 'EMERGENCY'];

export type ReferralTrack = 'STANDARD' | 'OVERSEAS_TREATMENT';

/** STANDARD: REFERRED->TRIAGED->SCHEDULED->SEEN. OVERSEAS_TREATMENT: REFERRED->BOARD_REVIEW->FINANCE_APPROVAL->LOGISTICS->TREATMENT->FOLLOWUP. REJECTED is a shared terminal state. */
export type ReferralState =
  | 'REFERRED'
  | 'TRIAGED'
  | 'SCHEDULED'
  | 'SEEN'
  | 'BOARD_REVIEW'
  | 'FINANCE_APPROVAL'
  | 'LOGISTICS'
  | 'TREATMENT'
  | 'FOLLOWUP'
  | 'REJECTED';

export interface ChecklistItem {
  item: string;
  done: boolean;
}

export interface OverseasTreatmentCase {
  id?: string;
  referralId?: string;
  destinationCountry?: string | null;
  destinationFacility?: string | null;
  boardDecision?: 'APPROVED' | 'DECLINED' | null;
  boardNotes?: string | null;
  boardReviewedBy?: string | null;
  boardReviewedAt?: string | null;
  financingAmount?: number | null;
  financingNotes?: string | null;
  financingApprovedBy?: string | null;
  financingApprovedAt?: string | null;
  logisticsChecklist: ChecklistItem[];
  travelDepartureDate?: string | null;
  travelReturnDate?: string | null;
  departedAt?: string | null;
  treatmentCompletedAt?: string | null;
  followUpNotes?: string | null;
}

export interface Referral {
  id: string;
  nin: string;
  fromFacilityId: string;
  fromFacility?: Facility;
  toSpecialty: string;
  priority: ReferralPriority;
  track: ReferralTrack;
  state: ReferralState;
  clinicalSummaryFileRef?: string | null;
  clinicalSummarySha256?: string | null;
  overseasCase: OverseasTreatmentCase | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export type PrescriptionState = 'ISSUED' | 'DISPENSED' | 'EXPIRED';

export interface PrescriptionItem {
  drug: string;
  dose: string;
  duration: string;
}

export interface Prescription {
  id: string;
  nin: string;
  encounterRef: string;
  facilityId: string;
  items: PrescriptionItem[];
  state: PrescriptionState;
  qrToken: string;
  expiresAt: string;
  dispensedByPharmacy?: string | null;
  dispensedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export type LabOrderStatus = 'ORDERED' | 'SENT_TO_LAB' | 'RESULT_READY' | 'CANCELLED';
export type LabResultReleaseStatus = 'PENDING_RELEASE' | 'RELEASED' | 'WITHHELD';

export interface LabObservation {
  code: string;
  display: string;
  value: string | number;
  unit?: string;
}

export interface LabResult {
  id: string;
  labOrderId: string;
  fhirPayload: unknown;
  resultSummary?: string | null;
  sensitiveClass: boolean;
  releaseStatus: LabResultReleaseStatus;
  releasedBy?: string | null;
  releasedAt?: string | null;
  withheldReason?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export interface LabOrder {
  id: string;
  nin: string;
  facilityId: string;
  referralId?: string | null;
  testCode: string;
  testName: string;
  priority: ReferralPriority;
  status: LabOrderStatus;
  externalLabRef?: string | null;
  fhirPayload: unknown;
  result?: LabResult | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export type VaccinationCertStatus = 'ISSUED' | 'REVOKED';

export interface VaccinationRecord {
  id: string;
  nin: string;
  antigen: string;
  doseNumber: number;
  administeredAt: string;
  batchNo: string;
  facilityId: string;
  certStatus: VaccinationCertStatus;
  docVaultId?: string | null;
  docVaultSerial?: string | null;
  qrToken?: string | null;
  sha256?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export type HealthDocKind = 'DISCHARGE_SUMMARY' | 'MEDICAL_CERTIFICATE';
export const HEALTH_DOC_KINDS: HealthDocKind[] = ['DISCHARGE_SUMMARY', 'MEDICAL_CERTIFICATE'];
export type HealthDocStatus = 'VALID' | 'REVOKED';
export type HealthDocReasonCategory = 'ILLNESS' | 'INJURY' | 'MATERNITY' | 'SURGERY' | 'MENTAL_HEALTH' | 'OTHER';
export const HEALTH_DOC_REASON_CATEGORIES: HealthDocReasonCategory[] = ['ILLNESS', 'INJURY', 'MATERNITY', 'SURGERY', 'MENTAL_HEALTH', 'OTHER'];

export interface HealthDocVaultLink {
  id: string;
  nin: string;
  kind: HealthDocKind;
  facilityId?: string | null;
  sourceReferralId?: string | null;
  reasonCategory?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  docVaultId: string;
  docVaultSerial: string;
  qrToken: string;
  sha256: string;
  status: HealthDocStatus;
  revokedAt?: string | null;
  revokedReason?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export type BirthSex = 'M' | 'F';
export type BirthPushStatus = 'PENDING_DISPATCH' | 'DISPATCHED' | 'FAILED';

export interface BirthNotification {
  id: string;
  motherNin: string;
  childNin?: string | null;
  childName?: string | null;
  sex: BirthSex;
  dob: string;
  facilityId: string;
  pushStatus: BirthPushStatus;
  dispatchedAt?: string | null;
  pushDetail?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────
// m25-public-health
// ─────────────────────────────────────────────────────────────────────────

export type ApplicantType = 'PERSON' | 'ENTITY';

export type PremisesType = 'FOOD_BUSINESS' | 'ACCOMMODATION' | 'SALON' | 'POOL';
export const PREMISES_TYPES: PremisesType[] = ['FOOD_BUSINESS', 'ACCOMMODATION', 'SALON', 'POOL'];

export type HealthCertState = 'SUBMITTED' | 'INSPECTION_SCHEDULED' | 'FEE_PENDING' | 'ISSUED' | 'REJECTED' | 'WITHDRAWN';
export const HEALTH_CERT_STATES: HealthCertState[] = ['SUBMITTED', 'INSPECTION_SCHEDULED', 'FEE_PENDING', 'ISSUED', 'REJECTED', 'WITHDRAWN'];

export type OperatingStatus = 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
export type HygieneGrade = 'A' | 'B' | 'C';

export interface HealthCertApplication {
  id: string;
  refNo: string;
  premisesType: PremisesType;
  premisesName: string;
  district: string;
  address?: string | null;
  applicantType: ApplicantType;
  applicantId: string;
  state: HealthCertState;
  certSerial?: string | null;
  certIssuedAt?: string | null;
  certExpiresAt?: string | null;
  docVaultId?: string | null;
  docVaultSha256?: string | null;
  currentGrade?: HygieneGrade | null;
  gradeQrToken?: string | null;
  gradeUpdatedAt?: string | null;
  operatingStatus: OperatingStatus;
  suspendedAt?: string | null;
  suspendReason?: string | null;
  revokedAt?: string | null;
  revokedReason?: string | null;
  feeIntentRefNo?: string | null;
  feeAmountScr?: number | null;
  feePaidAt?: string | null;
  rejectionReason?: string | null;
  withdrawnAt?: string | null;
  renewedFromId?: string | null;
  inspections?: HygieneInspection[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export type InspectionStatus = 'SCHEDULED' | 'COMPLETED' | 'CANCELLED';
export type InspectionSource = 'INITIAL' | 'RENEWAL' | 'ROUTINE' | 'COMPLAINT';
export const INSPECTION_SOURCES: InspectionSource[] = ['INITIAL', 'RENEWAL', 'ROUTINE', 'COMPLAINT'];

export interface ChecklistEntry {
  criterion: string;
  score: number;
  maxScore: number;
  notes?: string;
}

export interface InspectionPhoto {
  docRef: string;
  sha256: string;
  takenAt?: string;
}

export interface HygieneInspection {
  id: string;
  applicationId?: string | null;
  complaintId?: string | null;
  premisesName: string;
  district: string;
  source: InspectionSource;
  status: InspectionStatus;
  scheduledAt?: string | null;
  inspectedAt?: string | null;
  inspectorId?: string | null;
  checklist?: ChecklistEntry[] | null;
  totalScore?: number | null;
  maxScore?: number | null;
  grade?: HygieneGrade | null;
  criticalViolations: string[];
  notes?: string | null;
  photos?: InspectionPhoto[] | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export interface PublishedCriterion {
  code: string;
  label: string;
  maxScore: number;
}

export interface GradeResult {
  totalScore: number;
  maxScore: number;
  percentage: number;
  grade: HygieneGrade | null;
  passed: boolean;
}

/** POST /inspections/:id/result response: the inspection plus the computed grade and the (possibly updated) parent application. */
export interface InspectionResultResponse extends HygieneInspection {
  grading: GradeResult;
  application: HealthCertApplication | null;
}

export type FoodHandlerCertState =
  | 'APPLIED'
  | 'COURSE_BOOKED'
  | 'TRAINING_COMPLETED'
  | 'MEDICAL_SCREENED'
  | 'FEE_PENDING'
  | 'ISSUED'
  | 'REJECTED'
  | 'REVOKED';
export const FOOD_HANDLER_STATES: FoodHandlerCertState[] = [
  'APPLIED',
  'COURSE_BOOKED',
  'TRAINING_COMPLETED',
  'MEDICAL_SCREENED',
  'FEE_PENDING',
  'ISSUED',
  'REJECTED',
  'REVOKED',
];

export interface FoodHandlerCourseSession {
  id: string;
  venue: string;
  district: string;
  startsAt: string;
  capacity: number;
  bookedCount: number;
  spotsRemaining?: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export interface FoodHandlerCert {
  id: string;
  refNo: string;
  nin: string;
  courseSessionId?: string | null;
  state: FoodHandlerCertState;
  trainingCompletedAt?: string | null;
  medicalScreenPassed?: boolean | null;
  medicalScreenAt?: string | null;
  medicalScreenNotes?: string | null;
  feeIntentRefNo?: string | null;
  feeAmountScr?: number | null;
  feePaidAt?: string | null;
  rejectionReason?: string | null;
  certSerial?: string | null;
  issuedAt?: string | null;
  expiresAt?: string | null;
  docVaultId?: string | null;
  docVaultSha256?: string | null;
  revokedAt?: string | null;
  revokedReason?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export interface FoodHandlerCertVerifyResult {
  found: boolean;
  valid?: boolean;
  certSerial?: string | null;
  state?: FoodHandlerCertState;
  issuedAt?: string | null;
  expiresAt?: string | null;
}

export type ImportConsignmentType = 'FOOD' | 'PHARMA' | 'COSMETICS';
export const IMPORT_CONSIGNMENT_TYPES: ImportConsignmentType[] = ['FOOD', 'PHARMA', 'COSMETICS'];

export type ImportPermitStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'HOLD';
export const IMPORT_PERMIT_STATUSES: ImportPermitStatus[] = ['PENDING', 'APPROVED', 'REJECTED', 'HOLD'];

export interface ImportHealthPermit {
  id: string;
  refNo: string;
  consignmentType: ImportConsignmentType;
  importerType: ApplicantType;
  importerId: string;
  description: string;
  originCountry?: string | null;
  quantity?: string | null;
  consignmentRef?: string | null;
  status: ImportPermitStatus;
  dueBy: string;
  decidedAt?: string | null;
  decidedBy?: string | null;
  decisionNotes?: string | null;
  slaBreached: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export type ComplaintStatus = 'SUBMITTED' | 'TRIAGED' | 'INSPECTION_TRIGGERED' | 'RESOLVED' | 'DISMISSED';
export const COMPLAINT_STATUSES: ComplaintStatus[] = ['SUBMITTED', 'TRIAGED', 'INSPECTION_TRIGGERED', 'RESOLVED', 'DISMISSED'];

export interface SanitaryComplaint {
  id: string;
  refNo: string;
  applicationId?: string | null;
  premisesNameFreeText?: string | null;
  district?: string | null;
  complainantSubject?: string | null;
  complainantContact?: string | null;
  description: string;
  status: ComplaintStatus;
  triageNotes?: string | null;
  triagedAt?: string | null;
  triagedBy?: string | null;
  resolutionNotes?: string | null;
  resolvedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export type OutbreakStatus = 'ACTIVE' | 'CONTAINED' | 'CLOSED';
export const OUTBREAK_STATUSES: OutbreakStatus[] = ['ACTIVE', 'CONTAINED', 'CLOSED'];

export interface Outbreak {
  id: string;
  refNo: string;
  diseaseCode: string;
  districts: string[];
  description?: string | null;
  status: OutbreakStatus;
  declaredAt: string;
  declaredBy: string;
  containedAt?: string | null;
  closedAt?: string | null;
  notifications?: DiseaseNotification[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export type DiseaseNotificationState = 'NOTIFIED' | 'UNDER_INVESTIGATION' | 'CONFIRMED' | 'RULED_OUT' | 'CLOSED';
export const DISEASE_NOTIFICATION_STATES: DiseaseNotificationState[] = ['NOTIFIED', 'UNDER_INVESTIGATION', 'CONFIRMED', 'RULED_OUT', 'CLOSED'];

/** Controlled vocabulary this build wave accepts (packages/modules/m25-public-health/src/service/constants.ts). */
export const NOTIFIABLE_DISEASE_CODES = [
  'CHOLERA',
  'DENGUE',
  'COVID_19',
  'TUBERCULOSIS',
  'TYPHOID',
  'MEASLES',
  'LEPTOSPIROSIS',
  'CHIKUNGUNYA',
  'ZIKA',
  'HEPATITIS_A',
] as const;

export interface DiseaseNotification {
  id: string;
  refNo: string;
  nin: string;
  diseaseCode: string;
  onsetDate?: string | null;
  notifiedAt: string;
  reportingFacilityId: string;
  reportingFacilityName: string;
  reportingFacilityDistrict: string;
  reportingClinicianSubject: string;
  patientDistrict?: string | null;
  hospitalized: boolean;
  outcome?: string | null;
  state: DiseaseNotificationState;
  investigationNotes?: string | null;
  closedAt?: string | null;
  outbreakId?: string | null;
  outbreak?: Outbreak | null;
  contacts?: ContactTraceEntry[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export type ContactMonitoringStatus = 'MONITORING' | 'CLEARED' | 'SYMPTOMATIC';
export const CONTACT_MONITORING_STATUSES: ContactMonitoringStatus[] = ['MONITORING', 'CLEARED', 'SYMPTOMATIC'];

export interface ContactTraceEntry {
  id: string;
  notificationId: string;
  contactName: string;
  contactNin?: string | null;
  contactPhone?: string | null;
  exposureDate?: string | null;
  monitoringStatus: ContactMonitoringStatus;
  monitoringNotes?: string | null;
  clearedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export interface EpidemiologyDashboard {
  totalActiveCases: number;
  byDisease: Record<string, number>;
  byDistrict: Record<string, number>;
  activeOutbreaks: number;
  containedOutbreaks: number;
}

/** GET /public/grade-register (and /public/grade-register/:gradeQrToken) — no bearer token needed, deliberately thin (never applicant identity/fee/enforcement detail). */
export interface PublicGradeRegisterEntry {
  refNo: string;
  premisesName: string;
  premisesType: PremisesType;
  district: string;
  grade: HygieneGrade | null;
  gradeUpdatedAt?: string | null;
  operatingStatus: OperatingStatus;
}
